import React, { useEffect, useState } from 'react';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { CheckCircle, XCircle, Loader, AlertCircle } from 'lucide-react';

interface FileStatus {
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'rejected_duplicate';
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  totalRows?: number;
  recordsWritten?: number;
  skippedDuplicates?: number;
  supersededPrevious?: number;
  errors?: number;
  successRate?: number;
  hasWarning?: boolean;
  error?: string;
  // Set when the logical-key dedup guard rejected this file as a duplicate of
  // an already-ingested upload (queueFileUpload Gate B, mode=enforce).
  rejectionReason?: string;
  supersedesUploadId?: string;
  detectedPeriodMonth?: string;
  detectedRunNumber?: number;
}

interface VerificationResult {
  status: 'pending' | 'verified' | 'mismatch' | 'known_variance' | 'skipped';
  pdfNet?: number;
  bqNet?: number;
  netDiff?: number;
  verifiedAt?: Timestamp;
}

interface UploadStatus {
  fileName: string;
  status: string;
  filesQueued: number;
  filesProcessed: number;
  filesSucceeded: number;
  filesFailed: number;
  processing: Record<string, FileStatus>;
  errors?: Array<{ fileName: string; error: string }>;
  completedAt?: Timestamp;
  // Verification fields
  verificationStatus?: 'pending' | 'verified' | 'mismatch' | 'known_variance' | 'skipped';
  verificationResult?: VerificationResult;
}

interface ZipUploadMonitorProps {
  companyId: string;
  sourceDocId: string;
  onComplete?: () => void;
  onViewVerification?: () => void;
}

export const ZipUploadMonitor: React.FC<ZipUploadMonitorProps> = ({
  companyId,
  sourceDocId,
  onComplete,
  onViewVerification
}) => {
  const [status, setStatus] = useState<UploadStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const docRef = doc(db, `companies/${companyId}/commission_sources/${sourceDocId}`);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setError('Upload status document not found');
          return;
        }

        const data = snapshot.data() as UploadStatus;
        setStatus(data);

        // Call onComplete callback when finished
        if (data.completedAt && onComplete) {
          onComplete();
        }
      },
      (err) => {
        console.error('Error listening to upload status:', err);
        setError(err.message);
      }
    );

    return () => unsubscribe();
  }, [companyId, sourceDocId, onComplete]);

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
        <p className="text-red-400">Error monitoring upload: {error}</p>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-gray-800 p-6 rounded-lg">
        <div className="flex items-center gap-3">
          <Loader className="w-5 h-5 text-blue-500 animate-spin" />
          <p className="text-white">Loading upload status...</p>
        </div>
      </div>
    );
  }

  const progress = status.filesQueued > 0
    ? (status.filesProcessed / status.filesQueued) * 100
    : 0;

  const isComplete = status.filesProcessed === status.filesQueued;
  const hasErrors = status.filesFailed > 0;

  return (
    <div className="bg-gray-800 rounded-lg p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-white text-lg font-semibold">
          Processing: {status.fileName}
        </h3>
        {isComplete && (
          <div className={`flex items-center gap-2 px-3 py-1 rounded ${
            hasErrors ? 'bg-yellow-900/20 text-yellow-400' : 'bg-green-900/20 text-green-400'
          }`}>
            {hasErrors ? (
              <>
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Completed with errors</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Completed successfully</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div>
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>Progress</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-300 ${
              isComplete
                ? hasErrors
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
                : 'bg-blue-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gray-900/50 rounded p-3">
          <p className="text-gray-400 text-xs mb-1">Total Files</p>
          <p className="text-white text-2xl font-bold">{status.filesQueued}</p>
        </div>
        <div className="bg-gray-900/50 rounded p-3">
          <p className="text-gray-400 text-xs mb-1">Processed</p>
          <p className="text-white text-2xl font-bold">{status.filesProcessed}</p>
        </div>
        <div className="bg-gray-900/50 rounded p-3">
          <p className="text-green-400 text-xs mb-1">Succeeded</p>
          <p className="text-white text-2xl font-bold">{status.filesSucceeded}</p>
        </div>
        <div className="bg-gray-900/50 rounded p-3">
          <p className="text-red-400 text-xs mb-1">Failed</p>
          <p className="text-white text-2xl font-bold">{status.filesFailed}</p>
        </div>
      </div>

      {/* File List */}
      {status.processing && (
        <div>
          <h4 className="text-gray-400 text-sm font-medium mb-2">Files</h4>
          <div className="max-h-64 overflow-y-auto bg-gray-900/50 rounded p-3 space-y-2">
            {Object.entries(status.processing).map(([fileName, fileStatus]) => (
              <div key={fileName} className="flex items-center gap-3 py-1">
                {fileStatus.status === 'completed' && (
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                )}
                {fileStatus.status === 'failed' && (
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                )}
                {fileStatus.status === 'processing' && (
                  <Loader className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                )}
                {fileStatus.status === 'queued' && (
                  <div className="w-4 h-4 rounded-full border-2 border-gray-600 flex-shrink-0" />
                )}
                {fileStatus.status === 'rejected_duplicate' && (
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{fileName}</p>
                  {fileStatus.totalRows !== undefined && fileStatus.recordsWritten !== undefined && (
                    <div className="text-xs mt-0.5">
                      <p className={`${fileStatus.hasWarning ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {fileStatus.recordsWritten} / {fileStatus.totalRows} rows written
                        {fileStatus.successRate !== undefined && (
                          <span className={`ml-2 font-semibold ${
                            fileStatus.successRate < 90 ? 'text-yellow-400' : 'text-green-400'
                          }`}>
                            ({fileStatus.successRate}%)
                          </span>
                        )}
                      </p>
                      {fileStatus.skippedDuplicates! > 0 && (
                        <p className="text-yellow-400">
                          ⚠️ {fileStatus.skippedDuplicates} duplicates skipped
                        </p>
                      )}
                      {fileStatus.supersededPrevious! > 0 && (
                        <p className="text-blue-400">
                          🔄 {fileStatus.supersededPrevious} records replaced
                        </p>
                      )}
                    </div>
                  )}
                  {fileStatus.recordsWritten !== undefined && fileStatus.totalRows === undefined && (
                    <p className="text-xs text-gray-400">
                      {fileStatus.recordsWritten} records
                    </p>
                  )}
                  {fileStatus.error && (
                    <p className="text-xs text-red-400 mt-1">{fileStatus.error}</p>
                  )}
                  {fileStatus.status === 'rejected_duplicate' && (
                    <p className="text-xs text-amber-400 mt-1">
                      Duplicate of an already-uploaded file — not re-imported
                      {fileStatus.detectedPeriodMonth
                        ? ` (${fileStatus.detectedPeriodMonth}${fileStatus.detectedRunNumber ? ` Run ${fileStatus.detectedRunNumber}` : ''})`
                        : ''}
                    </p>
                  )}
                </div>

                <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                  fileStatus.status === 'completed' && fileStatus.hasWarning ? 'bg-yellow-900/30 text-yellow-400' :
                  fileStatus.status === 'completed' ? 'bg-green-900/30 text-green-400' :
                  fileStatus.status === 'failed' ? 'bg-red-900/30 text-red-400' :
                  fileStatus.status === 'processing' ? 'bg-blue-900/30 text-blue-400' :
                  fileStatus.status === 'rejected_duplicate' ? 'bg-amber-900/30 text-amber-400' :
                  'bg-gray-700 text-gray-400'
                }`}>
                  {fileStatus.status === 'completed' && fileStatus.hasWarning && '⚠️ '}
                  {fileStatus.status === 'rejected_duplicate' ? 'rejected (duplicate)' : fileStatus.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Errors Section */}
      {status.errors && status.errors.length > 0 && (
        <div className="bg-red-900/20 border border-red-500 rounded p-4">
          <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Errors ({status.errors.length})
          </h4>
          <div className="space-y-2">
            {status.errors.map((err, i) => (
              <div key={i} className="bg-black/30 rounded p-2">
                <p className="text-sm text-red-300 font-medium">{err.fileName}</p>
                <p className="text-xs text-red-400 mt-1">{err.error}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification Warning Banner */}
      {isComplete && status.verificationStatus === 'mismatch' && status.verificationResult && (
        <div className="bg-amber-900/20 border border-amber-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-amber-400 font-semibold mb-1">RCTI Verification Discrepancy</h4>
              <p className="text-sm text-amber-300/80 mb-3">
                Net commission differs by ${Math.abs(status.verificationResult.netDiff || 0).toFixed(2)}
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">PDF Net:</span>
                  <span className="text-white ml-2 font-mono">
                    ${(status.verificationResult.pdfNet || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">BigQuery Net:</span>
                  <span className="text-white ml-2 font-mono">
                    ${(status.verificationResult.bqNet || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              {onViewVerification && (
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={onViewVerification}
                    className="px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/30 transition-colors text-sm font-medium"
                  >
                    View Details
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Known Variance Banner */}
      {isComplete && status.verificationStatus === 'known_variance' && (
        <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-blue-400 font-semibold mb-1">Known Variance Detected</h4>
              <p className="text-sm text-blue-300/80">
                Referrer deductions differ from PDF totals. This is expected for multi-entity companies with cross-office referrals.
              </p>
              {onViewVerification && (
                <button
                  onClick={onViewVerification}
                  className="mt-3 px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-medium"
                >
                  View Details
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
